import React, { useState, useEffect } from 'react';
import { FileSearch, Filter } from 'lucide-react';
import { usersApi } from '../api/client';
import { fmtDate, fmtRelative } from '../utils/format';

const ACTION_CATEGORIES = {
  'user:role_change':   { cls: 'badge-saffron', label: 'Role changed' },
  'user:activate':      { cls: 'badge-green',   label: 'User toggled' },
  'assistant:ask':      { cls: 'badge-neutral', label: 'Assistant query' },
  'assistant:reset':    { cls: 'badge-neutral', label: 'Assistant reset' },
  'kms:gap_create':     { cls: 'badge-saffron', label: 'Gap created' },
  'kms:gap_update':     { cls: 'badge-saffron', label: 'Gap updated' },
  'kms:gap_delete':     { cls: 'badge-red',     label: 'Gap deleted' },
  'conversation:flag':  { cls: 'badge-saffron', label: 'Flag review' },
  'conversation:summarise': { cls: 'badge-neutral', label: 'Summary generated' },
};

const renderAction = (action) => {
  const cat = ACTION_CATEGORIES[action];
  if (cat) return <span className={`badge ${cat.cls}`}>{cat.label}</span>;
  return <code style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-600)' }}>{action}</code>;
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 200 };
    if (actionFilter) params.action = actionFilter;
    usersApi.audit(params)
      .then((data) => !cancelled && setLogs(data || []))
      .catch((e) => !cancelled && setError(e?.response?.data?.error || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [actionFilter]);

  return (
    <div>
      <div className="t-eyebrow">SRVC · Trail</div>
      <h1 className="t-display" style={{ fontSize: 38, marginBottom: 8 }}>
        <FileSearch size={26} style={{ verticalAlign: 'middle', marginRight: 10, color: 'var(--saffron-deep)' }} />
        Audit log.
      </h1>
      <p style={{ color: 'var(--ink-600)', fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, marginBottom: 24 }}>
        A record of administrative actions across the console — append-only, immutable.
      </p>

      <div className="panel" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Filter size={14} color="var(--ink-400)" />
        <select className="select" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={{ width: 220 }}>
          <option value="">All actions</option>
          {Object.entries(ACTION_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="panel" style={{ padding: '12px 16px', borderColor: 'var(--red)', background: 'var(--red-tint)', color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

      <div className="panel" style={{ overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 180 }}>When</th>
              <th>User</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Metadata</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32 }}><span className="loader" /></td></tr>
            )}
            {!loading && !logs.length && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-400)', fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
                No audit entries match this filter.
              </td></tr>
            )}
            {!loading && logs.map((log) => (
              <tr key={log.id}>
                <td style={{ fontSize: 12 }}>
                  <div>{fmtRelative(log.created_at)}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-400)', fontFamily: 'var(--mono)' }}>
                    {fmtDate(log.created_at)}
                  </div>
                </td>
                <td>
                  {log.user ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{log.user.name || log.user.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-400)' }}>{log.user.email}</div>
                    </>
                  ) : <span style={{ color: 'var(--ink-400)' }}>—</span>}
                </td>
                <td>{renderAction(log.action)}</td>
                <td style={{ fontSize: 12 }}>
                  {log.resource_type ? (
                    <>
                      <span style={{ color: 'var(--ink-700)' }}>{log.resource_type}</span>
                      {log.resource_id && <code style={{ fontSize: 10.5, color: 'var(--ink-400)', fontFamily: 'var(--mono)', marginLeft: 6 }}>
                        {String(log.resource_id).slice(0, 12)}…
                      </code>}
                    </>
                  ) : <span style={{ color: 'var(--ink-400)' }}>—</span>}
                </td>
                <td style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-600)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.metadata ? JSON.stringify(log.metadata).slice(0, 80) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
